import { getLanguage, localeOf, type ConsoleLanguage } from "./index";

/**
 * Dates, times, numbers and amounts in the staff member's language, through
 * `Intl`. Nothing in the console formats these by hand or with a fixed locale.
 * `language` defaults to the current one.
 */

type When = Date | string;

/** A bare day, with no time and no zone: "2026-10-15". */
const DAY_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A date, from a `Date` or a string.
 *
 * **A bare day is read as that day here, not as midnight UTC.** `new Date`
 * treats "2026-10-15" as UTC midnight, which is the 14th anywhere west of
 * Greenwich — so a review cycle closing on the 15th was showing as the 14th and
 * somebody starting on Monday was starting on Sunday. A day carries no zone
 * because it is not a moment, and turning it into one moves it.
 */
const toDate = (when: When): Date => {
  if (typeof when !== "string") return when;
  const day = DAY_ONLY.exec(when);
  return day ? new Date(`${when}T00:00:00`) : new Date(when);
};

/** "09:31" (fr), "09:31" (en), "09:31 ص" (ar). */
export function formatTime(when: When, language: ConsoleLanguage = getLanguage()): string {
  return toDate(when).toLocaleTimeString(localeOf(language), { hour: "2-digit", minute: "2-digit" });
}

/**
 * A time down to the second: "09:31:47".
 *
 * The audit reads at this precision because entries arrive in bursts — one
 * save writes a row per change, all within the same minute — and at minute
 * precision a column of identical times says nothing about their order.
 */
export function formatTimeExact(when: When, language: ConsoleLanguage = getLanguage()): string {
  return toDate(when).toLocaleTimeString(localeOf(language), {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

/** A date, with the options given: "17 sept. 2026" (fr), "17 Sept 2026" (en)… */
export function formatDate(
  when: When,
  options: Intl.DateTimeFormatOptions,
  language: ConsoleLanguage = getLanguage(),
): string {
  return toDate(when).toLocaleDateString(localeOf(language), options);
}

/** A date and time: "17 sept. 2026, 09:31" (fr). */
export function formatDateTime(when: When, language: ConsoleLanguage = getLanguage()): string {
  return toDate(when).toLocaleString(localeOf(language), { dateStyle: "medium", timeStyle: "short" });
}

/** "150 000" (fr), "150,000" (en), "150.000" (de). */
export function formatNumber(
  value: number,
  options?: Intl.NumberFormatOptions,
  language: ConsoleLanguage = getLanguage(),
): string {
  return new Intl.NumberFormat(localeOf(language), options).format(value);
}

/** An amount in Guinean francs, which has no smaller unit: "150 000 GNF" (fr), "GNF 150,000" (en). */
export function formatGnf(amount: number, language: ConsoleLanguage = getLanguage()): string {
  return formatNumber(
    amount,
    { style: "currency", currency: "GNF", currencyDisplay: "code", maximumFractionDigits: 0 },
    language,
  );
}

/**
 * An amount that reads back exactly, whatever the currency's usual precision.
 * A ceiling is not a price: "15 000,5 XOF" rounded to "15 001 XOF" would say
 * its holder may do more alone than they may (ADR-0019 §4).
 */
export function formatMoneyExact(amount: number, currency: string, language: ConsoleLanguage = getLanguage()): string {
  const decimals = (String(amount).split(".")[1] ?? "").length;
  try {
    const usual = new Intl.NumberFormat(localeOf(language), { style: "currency", currency }).resolvedOptions();
    const digits = Math.max(usual.maximumFractionDigits ?? 0, decimals);
    return formatNumber(
      amount,
      { style: "currency", currency, currencyDisplay: "code", maximumFractionDigits: digits },
      language,
    );
  } catch {
    return `${formatNumber(amount, undefined, language)} ${currency}`;
  }
}

/**
 * An amount in any currency, in its major unit, with the currency's code:
 * "25 000 GNF" (fr), "USD 2.00" (en). A code `Intl` doesn't know shows as the
 * number, then the code.
 */
export function formatMoney(amount: number, currency: string, language: ConsoleLanguage = getLanguage()): string {
  if (currency.toUpperCase() === "GNF") return formatGnf(amount, language);
  try {
    return formatNumber(amount, { style: "currency", currency, currencyDisplay: "code" }, language);
  } catch {
    return `${formatNumber(amount, undefined, language)} ${currency}`;
  }
}

/**
 * An ISO 3166-1 alpha-2 country code in the staff member's language: "GN" →
 * "Guinée" (fr), "Guinea" (en). A code `Intl` doesn't know shows as itself.
 */
export function formatCountry(code: string, language: ConsoleLanguage = getLanguage()): string {
  const upper = code.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(upper)) return code;
  try {
    return new Intl.DisplayNames([localeOf(language)], { type: "region" }).of(upper) ?? upper;
  } catch {
    return upper;
  }
}

/** A ratio as a percentage: 0.74 → "74 %" (fr), "74%" (en). */
export function formatPercent(ratio: number, language: ConsoleLanguage = getLanguage()): string {
  return formatNumber(ratio, { style: "percent", maximumFractionDigits: 0 }, language);
}

/** The `Intl` unit for each of the settings' units (shared/src/settings/value-types.ts), GNF aside. */
const INTL_UNITS = { km: "kilometer", minutes: "minute", hours: "hour", days: "day" } as const;

/**
 * A number in its unit, spelled out the way the language says it: "2 heures",
 * "90 jours", "30 minutes" (fr), "2 hours" (en). An amount in GNF goes
 * through formatGnf. `unit` is a setting's or rule's `Unit`.
 */
export function formatInUnit(
  value: number,
  unit: keyof typeof INTL_UNITS | "GNF",
  language: ConsoleLanguage = getLanguage(),
): string {
  if (unit === "GNF") return formatGnf(value, language);
  return formatNumber(
    value,
    { style: "unit", unit: INTL_UNITS[unit], unitDisplay: unit === "km" ? "short" : "long" },
    language,
  );
}
